import React, { useState, useEffect } from 'react';
import AdminContainer from '../components/layout/AdminContainer';
import ClientDetailPanel from '../components/admin/ClientDetailPanel';
import { useNotification } from '../context/NotificationContext';
import {
  fetchAdminClients, fetchAdminUsage, createClient, deleteClient,
  suspendClientAI, resumeClientAI, overrideSubscription
} from '../services/api';
import { Users, Plus, Search, Trash2 } from 'lucide-react';
import './AdminDashboardPage.css';

export default function AdminDashboardPage() {
  const { showSuccess, showError } = useNotification();
  const [clients, setClients] = useState([]);
  const [usageMap, setUsageMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', owner_phone_number: '', username: '', password: '' });
  const [creating, setCreating] = useState(false);
  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [clientsRes, usageRes] = await Promise.allSettled([
        fetchAdminClients(),
        fetchAdminUsage(),
      ]);

      if (clientsRes.status === 'fulfilled') {
        setClients(clientsRes.value.data);
      } else {
        setError('Failed to load clients.');
      }

      if (usageRes.status === 'fulfilled' && usageRes.value.data.clients) {
        const map = {};
        usageRes.value.data.clients.forEach(c => { map[c.id] = c; });
        setUsageMap(map);
      }

    } catch (err) {
      setError('Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await createClient(formData);
      setFormData({ name: '', owner_phone_number: '', username: '', password: '' });
      setShowCreateForm(false);
      loadData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      showError(typeof detail === 'string' ? detail : 'Failed to create client');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (clientId, clientName) => {
    if (!window.confirm(`Delete "${clientName}"? This removes all conversations, templates, and campaigns.`)) return;
    try {
      await deleteClient(clientId);
      loadData();
      if (selectedClient?.id === clientId) setSelectedClient(null);
    } catch (err) {
      showError('Failed to delete client.');
    }
  };

  const handleSuspendAI = async (clientId) => {
    try {
      await suspendClientAI(clientId);
      loadData();
      setSelectedClient(prev => prev ? { ...prev, ai_suspended: true } : null);
      showSuccess('AI suspended for this client.');
    } catch (err) {
      showError('Failed to suspend AI.');
    }
  };

  const handleResumeAI = async (clientId) => {
    try {
      await resumeClientAI(clientId);
      loadData();
      setSelectedClient(prev => prev ? { ...prev, ai_suspended: false } : null);
      showSuccess('AI resumed for this client.');
    } catch (err) {
      showError('Failed to resume AI.');
    }
  };

  const handleOverride = async (clientId, plan, duration) => {
    if (!window.confirm(`Override subscription to ${plan} for ${duration} days?`)) return;
    try {
      await overrideSubscription(clientId, { plan, duration_days: parseInt(duration) });
      // Reload so we get fresh subscription_expires_at from backend
      const [clientsRes, usageRes] = await Promise.allSettled([
        fetchAdminClients(),
        fetchAdminUsage(),
      ]);
      if (clientsRes.status === 'fulfilled') {
        const updated = clientsRes.value.data;
        setClients(updated);
        // Keep panel open with the updated client data
        const freshClient = updated.find(c => c.id === clientId);
        if (freshClient) {
          let usMap = usageMap;
          if (usageRes.status === 'fulfilled' && usageRes.value.data.clients) {
            const map = {};
            usageRes.value.data.clients.forEach(c => { map[c.id] = c; });
            setUsageMap(map);
            usMap = map;
          }
          const usage = usMap[clientId] || {};
          setSelectedClient({
            ...freshClient,
            messages_sent: usage.messages_sent,
            messages_received: usage.messages_received,
            last_active: usage.last_active,
            ai_suspended: usage.ai_suspended !== undefined ? usage.ai_suspended : freshClient.ai_suspended,
            whatsapp_connected: usage.whatsapp_connected || freshClient.whatsapp_connected || !!freshClient.whatsapp_number || !!freshClient.phone_number_id,
          });
        }
      }
      showSuccess('Subscription override saved.');
    } catch (err) {
      showError('Failed to override subscription.');
    }
  };

  const filteredClients = clients.filter(c =>
    !search || c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.username?.toLowerCase().includes(search.toLowerCase())
  );

  const enrichClient = (c) => {
    const usage = usageMap[c.id] || {};
    return {
      ...c,
      messages_sent: usage.messages_sent,
      messages_received: usage.messages_received,
      last_active: usage.last_active,
      ai_suspended: usage.ai_suspended !== undefined ? usage.ai_suspended : c.ai_suspended,
      whatsapp_connected: usage.whatsapp_connected || c.whatsapp_connected || !!c.whatsapp_number || !!c.phone_number_id,
    };
  };

  /**
   * Determine client stage:
   *   1. Signed Up — no Meta/WhatsApp connection
   *   2. Connected — has phone_number_id but no messages yet
   *   3. Active — connected + has messages
   */
  const getClientStage = (enriched) => {
    const hasConnection = !!enriched.phone_number_id || !!enriched.whatsapp_number || enriched.whatsapp_connected;
    const totalMessages = (enriched.messages_sent || 0) + (enriched.messages_received || 0);

    if (hasConnection && totalMessages > 0) {
      return { label: 'Active', color: '#22c55e', description: 'Using the app' };
    }
    if (hasConnection) {
      return { label: 'Connected', color: '#f59e0b', description: 'Meta linked, no messages yet' };
    }
    return { label: 'Signed Up', color: '#94a3b8', description: 'No Meta connection' };
  };

  const Badge = ({ color, children }) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      fontSize: '0.82rem', color: '#1a1b2e', fontWeight: 500
    }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }} />
      {children}
    </span>
  );

  return (
    <AdminContainer>
      <div className="admin-page">
        <div className="admin-page-header">
          <div>
            <h1><Users size={22} /> Client Monitoring</h1>
            <p>Manage and monitor all platform clients</p>
          </div>
          <button
            className="admin-btn primary"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            <Plus size={16} /> New Client
          </button>
        </div>

        {/* Create Form */}
        {showCreateForm && (
          <form className="admin-form-card" onSubmit={handleCreate}>
            <div className="admin-form-grid">
              <input
                placeholder="Client Name (e.g. Pied Piper)"
                value={formData.name}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                required
              />
              <input
                placeholder="Owner Phone (e.g. +91 98765 43210)"
                value={formData.owner_phone_number}
                onChange={e => setFormData(p => ({ ...p, owner_phone_number: e.target.value }))}
              />
              <input
                placeholder="Login Username"
                value={formData.username}
                onChange={e => setFormData(p => ({ ...p, username: e.target.value }))}
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={formData.password}
                onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                required
              />
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button type="submit" className="admin-btn primary" disabled={creating}>
                {creating ? 'Creating...' : 'Create Client'}
              </button>
              <button type="button" className="admin-btn" onClick={() => setShowCreateForm(false)}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {error && <div style={{ color: 'var(--danger)', marginBottom: 16, fontSize: '0.88rem' }}>{error}</div>}

        {/* Client Table */}
        <div className="admin-card">
          <div className="admin-card__header">
            <span className="admin-card__title">
              Clients ({filteredClients.length})
            </span>
            <div className="admin-search">
              <Search size={14} className="search-icon" />
              <input
                placeholder="Search clients..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <div className="admin-loading">Loading clients...</div>
          ) : filteredClients.length === 0 ? (
            <div className="admin-loading">No clients found.</div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Stage</th>
                    <th>WhatsApp</th>
                    <th>Messages</th>
                    <th>AI</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map(c => {
                    const enriched = enrichClient(c);
                    const isConnected = enriched.whatsapp_connected || !!c.whatsapp_number || !!c.phone_number_id;
                    const stage = getClientStage(enriched);
                    return (
                      <tr
                        key={c.id}
                        className={selectedClient?.id === c.id ? 'selected' : ''}
                        onClick={() => setSelectedClient(enriched)}
                      >
                        <td>
                          <div style={{ fontWeight: 600 }}>{c.name}</div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{c.username}</div>
                        </td>
                        <td>
                          <Badge color={stage.color} title={stage.description}>{stage.label}</Badge>
                        </td>
                        <td>
                          <Badge color={isConnected ? '#22c55e' : '#94a3b8'}>
                            {isConnected ? (enriched.whatsapp_number || c.whatsapp_number || 'Connected') : 'Not linked'}
                          </Badge>
                        </td>
                        <td>
                          {enriched.messages_sent != null
                            ? `${enriched.messages_sent + (enriched.messages_received || 0)}`
                            : '—'}
                        </td>
                        <td>
                          <span className={`status-dot ${enriched.ai_suspended ? 'suspended' : 'connected'}`}>
                            {enriched.ai_suspended ? 'Off' : 'On'}
                          </span>
                        </td>
                        <td>
                          <button
                            className="admin-btn danger"
                            style={{ padding: '4px 8px', fontSize: '0.78rem' }}
                            onClick={(e) => { e.stopPropagation(); handleDelete(c.id, c.name); }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selectedClient && (
          <ClientDetailPanel
            client={selectedClient}
            onClose={() => setSelectedClient(null)}
            onSuspendAI={handleSuspendAI}
            onResumeAI={handleResumeAI}
            onOverride={handleOverride}
          />
        )}
      </div>
    </AdminContainer>
  );
}
